/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useAsyncEffect } from "@cd2/components/build/es6"
import { IProductLocationStockResponseItem, useApi } from "../hooks/useApi"
import React, { useState } from "react"
import { Grid } from "@cd2/components"
import { useSettings } from "../utils/useSettings"
import { Link } from "react-router-dom"
import { buildRoute, eRoutes } from "../routes"
import { QuickToolTip } from "../Components/QuickTooltip"
import { IRecord } from "../types/schema"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { brandColors } from "../Components/EOSThemeProvider"
import { formatDate } from "@cd2/tools"

export function calculateStockShortfall(
  stock: IProductLocationStockResponseItem,
  settings?: {
    explicityAllocated?: boolean
    excludedStockFromAllocated?: number
  },
) {
  const allocationAmount = settings?.explicityAllocated
    ? stock.explicitlyAllocatedStock
    : stock.allocatedStock
  const shortfall =
    allocationAmount -
    (stock.inStock + stock.onPurchaseOrderStock) +
    (settings?.excludedStockFromAllocated ?? 0)
  return shortfall
}

export function calculateStockAvailable(
  stock: IProductLocationStockResponseItem,
  settings?: {
    explicityAllocated?: boolean
  },
) {
  return (
    (stock.inStock ?? 0) -
    ((settings?.explicityAllocated ? stock.explicitlyAllocatedStock : stock.allocatedStock) ?? 0)
  )
}

type IStockResponse = Record<string, IProductLocationStockResponseItem>

const STOCK_BATCH_WINDOW_MS = 10

let pendingValuesBatch: {
  ids: Set<string>
  promise: Promise<IStockResponse>
} | null = null

/**
 * Stock displays are rendered once per row (order lines, order request lines...), so a page
 * of 50 lines would otherwise fire 50 separate stock requests. Requests made within the
 * same short window are merged into a single call and each caller gets its own ids back.
 *
 * Only used for onlyValues lookups: those values are per location, whereas the full lookup's
 * onEosSalesOrderStock totals across every location in the request, so merging would change it.
 */
function fetchStockValuesBatched(
  fetchStock: (ids: string[]) => Promise<IStockResponse>,
  productLocationIds: string[],
): Promise<IStockResponse> {
  if (!pendingValuesBatch) {
    const ids = new Set<string>()
    const promise = new Promise<IStockResponse>((resolve, reject) => {
      setTimeout(() => {
        pendingValuesBatch = null
        fetchStock(Array.from(ids)).then(resolve, reject)
      }, STOCK_BATCH_WINDOW_MS)
    })
    pendingValuesBatch = { ids, promise }
  }
  productLocationIds.forEach(id => pendingValuesBatch!.ids.add(id))

  return pendingValuesBatch.promise.then(response => {
    if (response && "error" in response) {
      return response
    }
    const own: IStockResponse = {}
    for (const id of productLocationIds) {
      if (response[id]) {
        own[id] = response[id]
      }
    }
    return own
  })
}

export function useProductLocationStock(
  productLocationIds: string[],
  onDataLoaded?: (data: IStockResponse) => void,
  opts?: { onlyValues?: boolean },
) {
  const api = useApi()
  const [response, setResponse] = useState<IStockResponse | null>(null)

  useAsyncEffect(async () => {
    try {
      if (productLocationIds.length === 0) {
        setResponse({})
        onDataLoaded?.({})
        return
      }
      const response = opts?.onlyValues
        ? await fetchStockValuesBatched(
            ids => api.productLocationsStock(ids, { onlyValues: true }),
            productLocationIds,
          )
        : await api.productLocationsStock(productLocationIds, {
            onlyValues: opts?.onlyValues,
          })
      if (response && "error" in response) {
        throw new Error((response as any).error)
      }
      setResponse(response)
      onDataLoaded?.(response)
    } catch (e) {
      console.error("Error fetching stock", e)
      return
    }
  }, [JSON.stringify(productLocationIds)])

  return response
}

export function ProductLocationStockDisplaySwitcher(props: {
  productLocationId?: string
  productLocationIds?: string[]
  allocatedAction?: React.ReactNode
  orderId?: string
  unStyled?: boolean
  additionalAllocatedStock?: number
  orderIdForAllocationCheck?: string
  displayWithNoStock?: boolean
  showFull?: boolean
  /** Lay the summary out on one line (Allocated · Free stock · Show more) */
  inline?: boolean
}) {
  const [full, setFull] = useState(props.showFull ?? false)

  if (full) {
    return (
      <ProductLocationStockDisplay
        productLocationId={props.productLocationId}
        productLocationIds={props.productLocationIds}
        allocatedAction={props.allocatedAction}
        orderId={props.orderId}
        unStyled={props.unStyled}
        additionalAllocatedStock={props.additionalAllocatedStock}
        orderIdForAllocationCheck={props.orderIdForAllocationCheck}
        displayWithNoStock={props.displayWithNoStock}
      />
    )
  }

  return (
    <ProductLocationStockDisplaySimplified
      productLocationId={props.productLocationId}
      productLocationIds={props.productLocationIds}
      allocatedAction={props.allocatedAction}
      orderId={props.orderId}
      unStyled={props.unStyled}
      showMoreCb={() => setFull(true)}
      inline={props.inline}
      additionalAllocatedStock={props.additionalAllocatedStock}
      orderIdForAllocationCheck={props.orderIdForAllocationCheck}
      displayWithNoStock={props.displayWithNoStock}
    />
  )
}

export function ProductLocationStockDisplay(props: {
  productLocationId?: string
  productLocationIds?: string[]
  allocatedAction?: React.ReactNode
  orderId?: string
  unStyled?: boolean
  additionalAllocatedStock?: number
  orderIdForAllocationCheck?: string
  displayWithNoStock?: boolean
}) {
  const ids = props.productLocationIds
    ? props.productLocationIds
    : props.productLocationId
    ? [props.productLocationId]
    : undefined

  if (!ids || ids.length === 0 || ids.some(id => !id)) {
    if (props.displayWithNoStock) {
      return (
        <StockDisplay
          stockValues={combineProductLocationStockData([])}
          allocatedAction={props.allocatedAction}
          currentOrderId={props.orderId}
          unStyled={props.unStyled}
          additionalAllocatedStock={props.additionalAllocatedStock}
          orderIdForAllocationCheck={props.orderIdForAllocationCheck}
        />
      )
    }
    return null
  }

  const response = useProductLocationStock(ids)
  if (!response) {
    return null
  }

  const stock = props.productLocationId
    ? response[props.productLocationId]
    : combineProductLocationStockData(response)

  if (!stock) {
    return null
  }

  return (
    <StockDisplay
      stockValues={stock}
      allocatedAction={props.allocatedAction}
      currentOrderId={props.orderId}
      unStyled={props.unStyled}
      additionalAllocatedStock={props.additionalAllocatedStock}
      orderIdForAllocationCheck={props.orderIdForAllocationCheck}
    />
  )
}

export function ProductLocationStockDisplaySimplified(props: {
  productLocationId?: string
  productLocationIds?: string[]
  allocatedAction?: React.ReactNode
  orderId?: string
  unStyled?: boolean
  showMoreCb: () => void
  additionalAllocatedStock?: number
  orderIdForAllocationCheck?: string
  displayWithNoStock?: boolean
  inline?: boolean
}) {
  const ids = props.productLocationIds
    ? props.productLocationIds
    : props.productLocationId
    ? [props.productLocationId]
    : undefined

  if (!ids || ids.length === 0 || ids.some(id => !id)) {
    if (props.displayWithNoStock) {
      return (
        <StockDisplay
          stockValues={combineProductLocationStockData([])}
          allocatedAction={props.allocatedAction}
          currentOrderId={props.orderId}
          unStyled={props.unStyled}
          additionalAllocatedStock={props.additionalAllocatedStock}
          orderIdForAllocationCheck={props.orderIdForAllocationCheck}
          compact
        />
      )
    }
    return null
  }

  const response = useProductLocationStock(ids, undefined, { onlyValues: true })
  if (!response) {
    return <StockLoadingSkeleton />
  }

  const stock = props.productLocationId
    ? response[props.productLocationId]
    : combineProductLocationStockData(response)

  if (!stock) {
    return null
  }

  return (
    <div
      css={
        props.inline &&
        css`
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          column-gap: 12px;
        `
      }
    >
      <StockDisplay
        stockValues={stock}
        allocatedAction={props.allocatedAction}
        currentOrderId={props.orderId}
        unStyled={props.unStyled}
        additionalAllocatedStock={props.additionalAllocatedStock}
        orderIdForAllocationCheck={props.orderIdForAllocationCheck}
        compact
        inline={props.inline}
      />
      <a
        href="#"
        onClick={e => {
          e.preventDefault()
          props.showMoreCb()
        }}
        style={{ fontSize: 10, marginLeft: props.unStyled ? 0 : 4 }}
      >
        Show more
      </a>
    </div>
  )
}

function StockLoadingSkeleton() {
  return (
    <div
      css={css`
        width: 100px;
        height: 16px;
        background: ${brandColors.midGrey};
        border-radius: 4px;
      `}
    ></div>
  )
}

function asNumber(value: any) {
  if (isNaN(value)) {
    return 0
  }
  if (typeof value === "number") {
    return value
  }
  return 0
}

export function combineProductLocationStockData(
  data: Record<string, IProductLocationStockResponseItem> | IProductLocationStockResponseItem[],
  props?: { excludeOnOrdersFromId?: string },
) {
  const dataAsArray = Array.isArray(data) ? data : Object.values(data)
  const stock = dataAsArray.reduce(
    (acc, stock) => {
      const skipOnOrder = props?.excludeOnOrdersFromId && stock.id === props?.excludeOnOrdersFromId
      acc.id = ""
      acc.stock_code = ""
      acc.inStock += asNumber(stock.inStock)
      acc.allocatedStock += asNumber(stock.allocatedStock)
      acc.explicitlyAllocatedStock += asNumber(stock.explicitlyAllocatedStock)
      acc.onEosPurchaseOrderStock += asNumber(stock.onEosPurchaseOrderStock)
      acc.onSagePurchaseOrderStock += asNumber(stock.onSagePurchaseOrderStock)
      acc.onEosSalesOrderStock = asNumber(stock.onEosSalesOrderStock) // This is correct, the eos on order stock is the total of
      // all locations already
      acc.onSageSalesOrderStock += asNumber(stock.onSageSalesOrderStock)
      acc.avgMonthlySales = asNumber(stock.avgMonthlySales)
      acc.name = ""
      acc.onSalesOrderStock += asNumber(stock.onSalesOrderStock)
      acc.onPurchaseOrderStock += asNumber(skipOnOrder ? 0 : stock.onPurchaseOrderStock)
      acc.allocatedOrders = [...(acc.allocatedOrders ?? []), ...(stock.allocatedOrders ?? [])]
      acc.backOrders = [...(acc.backOrders ?? []), ...(stock.backOrders ?? [])]
      acc.onOrder = skipOnOrder ? acc.onOrder : [...(acc.onOrder ?? []), ...(stock.onOrder ?? [])]

      return acc
    },
    {
      id: "",
      stock_code: "",
      inStock: 0,
      allocatedStock: 0,
      explicitlyAllocatedStock: 0,
      onEosPurchaseOrderStock: 0,
      onSagePurchaseOrderStock: 0,
      onEosSalesOrderStock: 0,
      onSageSalesOrderStock: 0,
      availableStock: 0,
      availableInStock: 0,
      avgMonthlySales: 0,
      name: "",
      onSalesOrderStock: 0,
      onPurchaseOrderStock: 0,
      allocatedOrders: [],
      onOrder: [],
    } as IProductLocationStockResponseItem,
  )
  return stock
}

export function CombinedProductLocationStockDisplay(props: {
  productLocationIds: string[]
  allocatedAction?: React.ReactNode
  excludeOnOrdersFromId?: string
  unStyled?: boolean
}) {
  const response = useProductLocationStock(props.productLocationIds)
  if (!response) {
    return null
  }

  const stock = combineProductLocationStockData(response, {
    excludeOnOrdersFromId: props.excludeOnOrdersFromId,
  })

  return (
    <div>
      <StockDisplay
        stockValues={stock}
        allocatedAction={props.allocatedAction}
        unStyled={props.unStyled}
      />
    </div>
  )
}

export function ProductStockDisplay(props: { productId: string }) {
  const [productLocationIds, setProductLocationIds] = useState<string[]>([])
  const [consignmentWarehouseId, setConsignmentWarehouseId] = useState<string | undefined>()
  const skeem = window.skeem
  const settings = useSettings()
  const globalState = useGlobalState()

  const stockWarehouseIds = settings.stockWarehouses.map(sw => sw.id)

  useAsyncEffect(async () => {
    if (!settings._loaded) {
      return
    }
    const productLocations = await skeem.fetch2<{ id: string }[]>({
      model: {
        name: "product_locations",
        filter: {
          matches: {
            product: props.productId,
            warehouse:
              stockWarehouseIds && stockWarehouseIds.length ? stockWarehouseIds : undefined,
          },
        },
      },
    })
    const branch = await skeem.fetch2<IRecord<"branches", "consignmentWarehouse">>({
      first: {
        name: "branches",
        attributes: ["consignmentWarehouse"],
        filter: {
          matches: {
            id: globalState.currentUser.branch.id,
          },
        },
      },
    })
    if (branch.consignmentWarehouse) {
      setConsignmentWarehouseId(branch.consignmentWarehouse.id)
    }
    setProductLocationIds(productLocations.map(pl => pl.id))
  }, [props.productId, settings._loaded])

  if (!productLocationIds.length) {
    return null
  }

  return (
    <CombinedProductLocationStockDisplay
      productLocationIds={productLocationIds}
      excludeOnOrdersFromId={consignmentWarehouseId}
    />
  )
}

function* numberGenerator(start: number): Generator<number, void, unknown> {
  let num = start
  while (true) {
    yield num
    num++
  }
}

const nextNumberGenerator = numberGenerator(1)

export function StockDisplay(props: {
  stockValues?: IProductLocationStockResponseItem
  allocatedAction?: React.ReactNode
  unStyled?: boolean
  compact?: boolean
  inline?: boolean
  children?: React.ReactNode
  additionalAllocatedStock?: number
  currentOrderId?: string
  orderIdForAllocationCheck?: string
}) {
  const stock = props.stockValues
  const settings = useSettings()

  if (!stock) {
    console.error("StockDisplay: No stock values provided")
    return null
  }

  const orderAllocated =
    props.stockValues?.allocatedOrders?.find(x => x.id === props.orderIdForAllocationCheck)
      ?.quantity ?? 0
  const shortfall = calculateStockShortfall(stock, {
    explicityAllocated: settings.useExplicitAllocations,
    excludedStockFromAllocated: (props.additionalAllocatedStock ?? 0) - orderAllocated,
  })

  return (
    <div>
      <Grid
        gap={6}
        css={[
          props.unStyled
            ? css`
                white-space: nowrap;
                font-size: 12px;
                color: ${brandColors.darkNight};
                a {
                  text-decoration: underline;
                }
              `
            : css`
                white-space: nowrap;
                font-size: 12px;
                background: ${brandColors.lightGrey};
                border: 1px solid ${brandColors.midGrey};
                border-radius: 4px;
                padding: 6px;
                color: ${brandColors.darkNight};
                a {
                  color: white;
                  text-decoration: underline;
                }
              `,
          props.inline &&
            css`
              grid-auto-flow: column;
              grid-auto-columns: max-content;
            `,
        ]}
      >
        {props.compact ? (
          <span>
            Allocated{" "}
            <b>
              {settings.useExplicitAllocations
                ? stock.explicitlyAllocatedStock
                : stock.allocatedStock}
            </b>
          </span>
        ) : (
          <QuickToolTip
            content={
              (stock.allocatedOrders?.length ?? 0) > 0 ? (
                <Grid>
                  {stock.allocatedOrders?.map(order => (
                    <div key={order.id + "allocatedOrders" + nextNumberGenerator.next().value}>
                      <span>
                        {order.quantity}{" "}
                        {props?.currentOrderId && order.id === props?.currentOrderId ? (
                          "on this order"
                        ) : (
                          <span>
                            on {order.isDelivery ? "delivery for order" : "order"}{" "}
                            <Link
                              to={buildRoute(eRoutes["/orders/:id/preview"], {
                                id: order.id,
                              })}
                            >
                              {order.order_number}
                            </Link>
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {props.allocatedAction}
                </Grid>
              ) : (
                "No allocated stock"
              )
            }
            interactive
            showBorder
          >
            <span>
              Allocated{" "}
              <b>
                {settings.useExplicitAllocations
                  ? stock.explicitlyAllocatedStock
                  : stock.allocatedStock}
              </b>
            </span>
          </QuickToolTip>
        )}
        <span>
          {settings.useExplicitAllocations ? (
            <span>
              Free stock <b>{stock.inStock - stock.explicitlyAllocatedStock}</b>{" "}
            </span>
          ) : (
            <span>
              In-stock <b>{stock.inStock}</b>{" "}
            </span>
          )}
        </span>
        {!!stock.provisionalStock && (
          <QuickToolTip
            content={`${stock.provisionalStock} of this stock was booked in on a goods receipt and is being held provisionally until a Sage import confirms it arrived. If it is not confirmed after 3 imports it is removed from stock and flagged.`}
            showBorder
          >
            <span
              css={css`
                color: ${brandColors.orange};
                cursor: help;
              `}
            >
              ({stock.provisionalStock} provisional)
            </span>
          </QuickToolTip>
        )}
        {!props.compact && (
          <QuickToolTip
            maxWidth="450px"
            content={
              stock.onOrder && stock.onOrder.length > 0 ? (
                <Grid
                  css={css(
                    `
                      width: max-content;
                    `,
                  )}
                >
                  {stock.onOrder.map(order => {
                    const orderReference = order.id ? (
                      <span>
                        <span>Fortus Live</span>{" "}
                        <Link to={buildRoute(eRoutes["/purchase-orders/:id"], { id: order.id })}>
                          PO {order.order_number}
                        </Link>
                      </span>
                    ) : (
                      `SAGE PO ${order.order_number}`
                    )
                    return (
                      <div key={order.id + "onOrder" + nextNumberGenerator.next().value}>
                        <span>
                          {order.quantity} on {orderReference}
                          {order.requested_delivery_date
                            ? ` expected ${formatDate(new Date(order.requested_delivery_date))}`
                            : ""}
                          {order.created_at
                            ? ` created ${formatDate(new Date(order.created_at))}`
                            : ""}
                        </span>
                      </div>
                    )
                  })}
                </Grid>
              ) : (
                "None on order"
              )
            }
            interactive
            showBorder
          >
            <span>
              On-order <b>{stock.onPurchaseOrderStock}</b>
            </span>
          </QuickToolTip>
        )}
        {!props.compact && (
          <React.Fragment>
            <span>
              Avg Sales (30 days) <b>{stock.avgMonthlySales}</b>
            </span>
            {shortfall > 0 && (
              <span
                css={css`
                  color: red;
                `}
              >
                Shortfall <b>{shortfall}</b>
              </span>
            )}
          </React.Fragment>
        )}
        {props.children}
      </Grid>
    </div>
  )
}
